import { useState } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import MaskedInputService from 'services/maskedInput'
import Select from 'components/Inputs/Select'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import customerValidator from 'validators/customerValidator'
import { DateTime } from 'luxon'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  Input,
  MaskedInput,
  Row,
  Label,
  WarningText,
  ErrorContainer
} from './styles'

export default function EditCustomerModal ({
  visible,
  data: customer,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='lg' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Editar cliente</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={customerValidator}
          validateOnChange={false}
          validateOnBlur={true}
          initialValues={{
            'id': customer?.id,
            'name': customer?.name ?? '',
            'email': customer?.email,
            'phone': customer?.phone,
            'gender': customer?.gender ? {
              label: customer?.gender, value: customer?.gender
            } : null,
            'birth': customer?.birth 
              ? DateTime.fromISO (customer?.birth).toFormat ('ddMMyyyy')
              : ''
          }}
          onSubmit={async (values, { setSubmitting }) => {
            setErrorMessage (null)
            setSubmitting (true)

            try {
              const { data } = await cutileiApi.put (
                `/businesses/${businessId}/customers/${customer.id}`, {
                  ...values,
                  gender: values.gender?.value ?? null,
                  birth: values.birth !== ''
                    ? DateTime.fromFormat (values.birth, 'ddMMyyyy').toISODate ()
                    : null
                }, requestConfig
              )

              setSubmitting (false)
              handleConfirm ({...data, name: values.name})
              handleClose ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit
          }) => {
            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                    <Row>
                      <FormField>
                        <Label htmlFor='name' style={{marginBottom: 4}}>
                          Nome
                        </Label>
                        <Input
                          id='name'
                          placeholder='Nome'
                          value={values.name}
                          onChange={handleChange ('name')} 
                          onBlur={handleBlur ('name')}
                        />
                      </FormField>
                      <FormField>
                        <Label htmlFor='email' style={{marginBottom: 4}}>
                          E-mail
                        </Label>
                        <Input
                          id='email'
                          type='email'
                          placeholder='E-mail'
                          value={values.email}
                          onChange={handleChange ('email')} 
                          onBlur={handleBlur ('email')}
                        />
                        {touched.email && errors.email && (
                          <WarningText>
                            {errors.email}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Row>
                      <FormField>
                        <Label htmlFor='phone' style={{marginBottom: 4}}>
                          Celular
                        </Label>
                        <MaskedInput
                          id='phone'
                          name='phone'
                          placeholder='Celular'
                          options={{
                            blocks: [0, 2, 5, 4],
                            delimiters: ['(', ') ', '-'],
                            numericOnly: true
                          }}
                          value={values.phone}
                          onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                          onBlur={handleBlur ('phone')}
                        />
                        {touched.phone && errors.phone && (
                          <WarningText>
                            {errors.phone}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <Label htmlFor='gender' style={{marginBottom: 4}}>
                          Gênero
                        </Label>
                        <Select
                          id='gender'
                          name='gender'
                          placeholder='Gênero...'
                          value={values.gender}
                          options={[
                            {label: 'Masculino', value: 'Masculino'},
                            {label: 'Feminino', value: 'Feminino'},
                            {label: 'Não informar', value: 'Não informar'}
                          ]}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          error={errors.gender}
                          touched={touched.gender}
                        />
                        {touched.gender && errors.gender && (
                          <WarningText>{errors.gender}</WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <Label htmlFor='birth' style={{marginBottom: 4}}>
                          Data de nascimento
                        </Label>
                        <MaskedInput
                          id='birth'
                          name='birth'
                          placeholder='Nascimento'
                          options={{date: true, datePattern: ['d', 'm', 'Y']}}
                          value={values.birth}
                          onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                          onBlur={handleBlur ('birth')}
                        />
                        {touched.birth && errors.birth && (
                          <WarningText>{errors.birth}</WarningText>
                        )}
                      </FormField>
                    </Row>
                    {errorMessage && (
                      <ErrorContainer>
                        <p>{errorMessage}</p>
                      </ErrorContainer>
                    )}
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label='Salvar alterações'
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={180}
                  />
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
